/**
 * Componente EditAttendanceModal
 * Modal para edição de chamadas, permitindo alterar presença e adicionar comentários
 */

import React, { useState, useEffect, useRef } from 'react';
import api from '../../../../services/api';
import './EditAttendanceModal.css';
import { FaClock, FaChalkboardTeacher, FaUserCircle } from 'react-icons/fa';
import ImageCropModal from '../../../Shared/ImageCropModal';
import LoadingSpinner from '../../../LoadingSpinner/LoadingSpinner';

// Adicionar função utilitária no início do arquivo
const formatDateToBR = (dateString) => {
    if (!dateString) return 'N/A';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
};

function EditAttendanceModal({ session, onClose, onSave }) {
  const [attendanceData, setAttendanceData] = useState({});
  const [sessionInfo, setSessionInfo] = useState({});
  const [comments, setComments] = useState({});
  const [error, setError] = useState(null);
  const [students, setStudents] = useState([]);
  const [focusedStudentIndex, setFocusedStudentIndex] = useState(0);
  const selectRefs = useRef([]);
  const [photoErrors, setPhotoErrors] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState({});
  const [cropModal, setCropModal] = useState({ open: false, studentId: null, imageSrc: null });
  const [isLoading, setIsLoading] = useState(true);
  
  // Desabilita o scrolling do body quando o modal está aberto
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  /**
   * Busca dados de presença e alunos ao inicializar
   */
  useEffect(() => {
    const fetchAttendanceAndStudents = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(`/attendances/sessions/${session}/records`);
        const sessionData = response.data.data;
        if (!sessionData) {
          throw new Error('Dados da sessão não encontrados.');
        }

        setSessionInfo(sessionData);
        // Corrige caso attendances venha como objeto numerado
        const attendancesArray = Array.isArray(sessionData.attendances)
          ? sessionData.attendances
          : Object.values(sessionData.attendances);
        const studentList = attendancesArray.map(attendance => ({
          id: attendance.student_id,
          first_name: attendance.student_first_name,
          last_name: attendance.student_last_name,
          photo_url: attendance.student_photo_url || null
        }));

        const sortedStudents = studentList.sort((a, b) => {
          const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
          const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setStudents(sortedStudents);

        const attendanceMap = {};
        const commentMap = {};
        attendancesArray.forEach(attendance => {
          attendanceMap[attendance.student_id] = attendance.status || 'P';
          commentMap[attendance.student_id] = attendance.comments || '';
        });
        setAttendanceData(attendanceMap);
        setComments(commentMap);
      } catch (error) {
        console.error('Erro ao buscar dados de presença e alunos:', error);
        setError('Erro ao carregar dados de presença e alunos.');
      } finally {
        setIsLoading(false);
      }
    };

    if (session) {
      fetchAttendanceAndStudents();
    }
  }, [session]);

  /**
   * Handlers de navegação e interação
   */
  const handleKeyDown = (e, index) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const nextIndex = (index + 1) % students.length;
      setFocusedStudentIndex(nextIndex);
      selectRefs.current[nextIndex]?.focus();
    }
  };

  const handleSelectKeyDown = (e, studentId) => {
    const statusOptions = ['P', 'F', 'FJ', 'PA'];
    const currentStatus = attendanceData[studentId] || 'P';
    const currentIndex = statusOptions.indexOf(currentStatus);

    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevIndex = (currentIndex - 1 + statusOptions.length) % statusOptions.length;
      handleAttendanceChange(studentId, statusOptions[prevIndex]);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % statusOptions.length;
      handleAttendanceChange(studentId, statusOptions[nextIndex]);
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const handleCommentChange = (studentId, comment) => {
    setComments(prev => ({
      ...prev,
      [studentId]: comment
    }));
  };

  /**
   * Handlers de submissão
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/attendances/session/${session}/update`, {
        id: session,
        attendances: students.map(student => ({
          student_id: student.id,
          status: attendanceData[student.id] || 'P',
          comments: comments[student.id] || null
        }))
      });

      if (response.status === 200) {
        alert('Presença atualizada com sucesso!');
        onSave(response.data);
        onClose();
      } else {
        setError('Erro ao atualizar presença.');
      }
    } catch (error) {
      console.error('Erro ao atualizar presença:', error);
      setError('Erro ao atualizar presença.');
    }
  };

  const editAttedanceSave = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/attendances/session/${session}/save`, {
        id: session,
        attendances: students.map(student => ({
          student_id: student.id,
          status: attendanceData[student.id] || 'P',
          comments: comments[student.id] || null
        }))
      });

      if (response.status === 200) {
        alert('Presença atualizada com sucesso!');
        onSave(response.data);
        onClose();
      } else {
        setError('Erro ao atualizar presença.');
      }
    } catch (error) {
      console.error('Erro ao atualizar presença:', error);
      setError('Erro ao atualizar presença.');
    }
  };

  const handleSelectPhoto = (studentId, e) => {
    const file = e.target.files[0];
    const types = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (file && !types.includes(file.type)) {
      setPhotoErrors(prev => ({ ...prev, [studentId]: "Formato inválido. JPEG, JPG, PNG ou GIF." }));
      return;
    } else {
      setPhotoErrors(prev => ({ ...prev, [studentId]: "" }));
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCropModal({ open: true, studentId, imageSrc: ev.target.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    const { studentId } = cropModal;
    setCropModal({ open: false, studentId: null, imageSrc: null });
    setUploadingPhoto(prev => ({ ...prev, [studentId]: true }));
    try {
      const res = await api.uploadPhoto(studentId, croppedBlob);
      setStudents(prev => prev.map(s => s.id === studentId ? { ...s, photo_url: res.data.photo_url } : s));
    } catch (err) {
      setPhotoErrors(prev => ({ ...prev, [studentId]: "Erro ao enviar foto." }));
    } finally {
      setUploadingPhoto(prev => ({ ...prev, [studentId]: false }));
    }
  };

  // Componente genérico para carregar imagens com fallback para Google Drive.
  const PhotoWithFallback = ({ photoUrl, alt, className, onError, onLoad, style }) => {
    const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [urls, setUrls] = useState([]);

    // Gera múltiplas URLs de fallback para o Google Drive
    useEffect(() => {
      if (!photoUrl) {
        setUrls([]);
        return;
      }

      if (!photoUrl.includes('drive.google.com')) {
        setUrls([photoUrl]);
        return; // URLs não-Drive não precisam de múltiplas tentativas
      }
      
      const fileIdMatch = photoUrl.match(/[?&]id=([a-zA-Z0-9-_]+)/) || photoUrl.match(/\/d\/([a-zA-Z0-9-_]+)/);
      if (!fileIdMatch) {
        setUrls([photoUrl]);
        return;
      }
      
      const fileId = fileIdMatch[1];
      
      setUrls([
        // Primeira tentativa: GoogleUserContent (geralmente funciona melhor)
        `https://lh3.googleusercontent.com/d/${fileId}`,
        // Segunda tentativa: formato original mas com diferentes parâmetros
        `https://drive.google.com/uc?id=${fileId}&export=download`,
        // Terceira tentativa: usando thumbnail (menor qualidade mas mais confiável)
        `https://drive.google.com/thumbnail?id=${fileId}&sz=w400-h400`,
        // Quarta tentativa: direct link
        `https://drive.google.com/uc?export=view&id=${fileId}`,
        // Quinta tentativa: formato original
        photoUrl
      ]);
    }, [photoUrl]);

    const handleImageError = () => {
      console.log(`Erro ao carregar URL ${currentUrlIndex + 1}/${urls.length}: ${urls[currentUrlIndex]}`);
      setIsLoading(false);
      if (currentUrlIndex < urls.length - 1) {
        setCurrentUrlIndex(prev => prev + 1);
        setIsLoading(true); // Reset loading para a próxima tentativa
      } else {
        console.log('Todas as tentativas de carregamento falharam para:', photoUrl);
        setHasError(true);
        if (onError) onError();
      }
    };

    const handleImageLoad = () => {
      console.log(`Sucesso ao carregar URL ${currentUrlIndex + 1}/${urls.length}: ${urls[currentUrlIndex]}`);
      setIsLoading(false);
      setHasError(false);
      if (onLoad) onLoad();
    };

    // Reset quando a URL muda
    useEffect(() => {
      setCurrentUrlIndex(0);
      setHasError(false);
      setIsLoading(true);
    }, [photoUrl]);

    if (!photoUrl || hasError || !urls.length) {
      return null;
    }

    return (
      <img
        src={urls[currentUrlIndex]}
        alt={alt}
        className={`${className} ${isLoading ? 'student-photo-loading' : ''}`}
        style={style}
        onError={handleImageError}
        onLoad={handleImageLoad}
        referrerPolicy="no-referrer"
      />
    );
  };

  const isMobile = window.innerWidth <= 768;
  // Função para obter a imagem do avatar ou usar um ícone padrão
  const getAvatarOrIcon = (student) => {
    const studentName = `${student.first_name} ${student.last_name}`;
    const cellId = `attendance-photo-cell-${student.id}`;
    
    if (student.photo_url) {
      return (
        <div className="attendance-photo-cell" id={cellId}>
          <PhotoWithFallback
            photoUrl={student.photo_url}
            alt={studentName}
            className="student-photo attendance-student-photo"
            style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '50%', objectPosition: 'center' }}
            onError={() => {
              // Mostra o ícone de fallback quando todas as tentativas falharem
              const cell = document.getElementById(cellId);
              if (cell) {
                const img = cell.querySelector('.student-photo');
                const fallback = cell.querySelector('.attendance-photo-fallback');
                if (img) img.style.display = 'none';
                if (fallback) fallback.style.display = 'block';
              }
            }}
            onLoad={() => {
              // Esconde o ícone de fallback quando a imagem carrega
              const cell = document.getElementById(cellId);
              if (cell) {
                const fallback = cell.querySelector('.attendance-photo-fallback');
                if (fallback) fallback.style.display = 'none';
              }
            }}
          />
          <FaUserCircle 
            size={56} 
            className="attendance-avatar-icon attendance-photo-fallback"
            style={{display: 'none'}}
          />
        </div>
      );
    } else {
      return <FaUserCircle size={56} className="attendance-avatar-icon" />;
    }
  };
  // Função que determina o status do aluno em formato texto
  const getStatusText = (status) => {
    // Versão mais curta para mobile
    if (isMobile) {
      switch(status) {
        case 'P': return 'Presente';
        case 'F': return 'Falta';
        case 'FJ': return 'F. Justificada';
        case 'PA': return 'P. Atraso';
        default: return 'Presente';
      }
    } else {
      // Versão desktop mantém textos completos
      switch(status) {
        case 'P': return 'Presente';
        case 'F': return 'Falta';
        case 'FJ': return 'Falta Justificada';
        case 'PA': return 'Presente com Atraso';
        default: return 'Presente';
      }
    }
  };
  
  // Função para obter a cor do status com base nas variáveis CSS do root
  const getStatusColor = (status) => {
    switch(status) {
      case 'P': return 'var(--primary-color)';
      case 'F': return 'var(--error-color)';
      case 'FJ': return 'var(--secondary-color)';
      case 'PA': return 'var(--link-color)';
      default: return 'var(--primary-color)';
    }
  };
  
  // Função para alternar o status ao clicar no card do aluno em mobile
  const handleMobileStatusToggle = (studentId) => {
    const currentStatus = attendanceData[studentId] || 'P';
    const statusOrder = ['P', 'F', 'FJ', 'PA'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    const nextIndex = (currentIndex + 1) % statusOrder.length;
    
    handleAttendanceChange(studentId, statusOrder[nextIndex]);
  };

  return (
    <div className="attendance-modal-overlay" onClick={onClose}>
      <div className={`attendance-modal ${isMobile ? 'mobile-view' : ''}`} onClick={(e) => e.stopPropagation()}>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {isMobile ? (
              // Layout Mobile (conforme imagem)
              <div className="mobile-attendance">
                <div className="workshop-details">
                  <h2>Detalhes da Oficina</h2>
                  <div className="detail-item">
                    <FaChalkboardTeacher className="detail-icon" />
                    <div>
                      <div className="detail-title">{sessionInfo.category_name || 'Oficina Cultural'}</div>
                    </div>
                  </div>
                  <div className="detail-item">
                    <FaClock className="detail-icon" />
                    <div>
                      <div className="detail-title">Horário: {sessionInfo.start_time || '10:00'}</div>
                      <div className="detail-subtitle">Data: {formatDateToBR(sessionInfo.date) || '15/03/2024'}</div>
                    </div>
                  </div>
                </div>
                
                <div className="attendance-divider">
                  <h3>Alunos</h3>
                </div>
                
                <div className="mobile-students-list">
                  {error && <div className="attendance-error-message">{error}</div>}
                  <form onSubmit={handleSubmit}>
                    {students.map((student, index) => {
                      const status = attendanceData[student.id] || 'P';
                      return (
                        <div key={student.id} className="student-card">
                          <div 
                            className="student-card-header"
                            onClick={() => handleMobileStatusToggle(student.id)}
                          >
                            <div className="student-avatar">
                              {getAvatarOrIcon(student)}
                            </div>
                            <div className="student-info">
                              <div className="student-name">{student.first_name} {student.last_name}</div>                          
                              <div className="comments-preview">
                                {comments[student.id] ? comments[student.id] : 'Sem comentários'}
                              </div>
                            </div>
                            <div 
                              className="student-status"
                              style={{ backgroundColor: getStatusColor(status) }}
                            >
                              {getStatusText(status)}
                            </div>
                          </div>
                          {/* Upload/alteração de foto */}
                          <div className="student-photo-upload-controls" style={{ margin: '10px 0' }}>
                            <label className="student-photo-upload-btn" htmlFor={`photo-upload-${student.id}`}>
                              {uploadingPhoto[student.id] ? 'Enviando...' : (student.photo_url ? 'Alterar foto' : 'Adicionar foto')}
                            </label>
                            <input
                              id={`photo-upload-${student.id}`}
                              type="file"
                              accept=".jpeg,.jpg,.png,.gif"
                              onChange={(e) => handleSelectPhoto(student.id, e)}
                              style={{ display: 'none' }}
                              disabled={uploadingPhoto[student.id]}
                            />
                            {photoErrors[student.id] && (
                              <span className="student-file-error">{photoErrors[student.id]}</span>
                            )}
                          </div>
                          <div className="student-comments-container">
                            <input
                              type="text"
                              placeholder="Adicionar comentário..."
                              value={comments[student.id] || ''}
                              onChange={(e) => handleCommentChange(student.id, e.target.value)}
                              className="student-comment-input"
                            />
                          </div>
                        </div>
                      );
                    })}
                    <div className="mobile-action-buttons">
                      <button type="submit" className="mobile-action-button mobile-submit">Finalizar</button>
                      <button 
                        type="button" 
                        onClick={editAttedanceSave} 
                        className="mobile-action-button mobile-save"
                      >
                        Salvar
                      </button>
                      <button 
                        type="button" 
                        onClick={onClose} 
                        className="mobile-action-button mobile-cancel"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              // Layout Desktop (Original)
              <>
                <div className="attendance-modal-header">
                  <h2>Editar Chamada</h2>
                  <div className="attendance-form-group">
                    <label>Data: {formatDateToBR(sessionInfo.date)}</label>
                  </div>
                  <div className="attendance-form-group">
                    <label>Oficina: {sessionInfo.course_name}</label>
                  </div>
                </div>
                <div className="attendance-modal-body">
                  {error && <div className="attendance-error-message">{error}</div>}
                  <form onSubmit={handleSubmit}>
                    <div className="attendance-form-group">
                      <label>Alunos:</label>
                      <table className="attendance-students-table">
                        <thead>
                          <tr>
                            <th>Nome</th>
                            <th>Status</th>
                            <th>Comentários</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.map((student, index) => (
                            <tr
                              key={student.id}
                              className={`attendance-student-item ${focusedStudentIndex === index ? 'focused' : ''}`}
                              onKeyDown={(e) => handleKeyDown(e, index)}
                            >
                              <td className="attendance-student-name">{student.first_name} {student.last_name}</td>
                              <td>
                                <select
                                  ref={(el) => (selectRefs.current[index] = el)}
                                  value={attendanceData[student.id] || 'P'}
                                  onChange={(e) => handleAttendanceChange(student.id, e.target.value)}
                                  onKeyDown={(e) => handleSelectKeyDown(e, student.id)}
                                  className="attendance-attendance-select"
                                  tabIndex={focusedStudentIndex === index ? 0 : -1}
                                >
                                  <option value="P">Presente</option>
                                  <option value="F">Falta</option>
                                  <option value="FJ">Falta Justificada</option>
                                  <option value="PA">Presente com Atraso</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  placeholder="Comentários (opcional)"
                                  value={comments[student.id] || ''}
                                  onChange={(e) => handleCommentChange(student.id, e.target.value)}
                                  className="attendance-comment-input"
                                  tabIndex={focusedStudentIndex === index ? 0 : -1}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="modal-buttons">
                      <button type="submit" className="attendance-save-button">Finalizar</button>
                      <button type="button" onClick={editAttedanceSave} className="attendance-save-button">Salvar edição</button>
                      <button type="button" onClick={onClose} className="attendance-cancel-button">
                        Cancelar
                      </button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </>
        )}
        {/* Modal de crop de imagem */}
        <ImageCropModal
          open={cropModal.open}
          image={cropModal.imageSrc}
          onClose={() => setCropModal({ open: false, studentId: null, imageSrc: null })}
          onCropComplete={handleCropComplete}
        />
      </div>
    </div>
  );
}

export default EditAttendanceModal;